/**
 * User settings — persisted to localStorage across sessions.
 * UI: src/ui/settings-screen.ts
 */
export type MovementMode = 'world' | 'nose';
export type Language = 'tr' | 'en';
/** Floating damage numbers (docs/12 E1): hidden / crits only / all (merged) */
export type DamageNumbersMode = 'off' | 'crits' | 'all';
export type ShakeLevel = 'off' | 'low' | 'full';
export type EffectsLevel = 'low' | 'medium' | 'high';

export const DAMAGE_NUMBER_MODES: readonly DamageNumbersMode[] = ['off', 'crits', 'all'];
export const SHAKE_LEVELS: readonly ShakeLevel[] = ['off', 'low', 'full'];
export const EFFECTS_LEVELS: readonly EffectsLevel[] = ['low', 'medium', 'high'];

export interface Settings {
  /** 'world': screen-relative input (twin-stick). 'nose': forward = facing direction */
  movementMode: MovementMode;
  /** When no aim input is given, lock aim onto the nearest enemy */
  autoAim: boolean;
  sound: boolean;
  /** Fullscreen preference — asked ONCE via the in-game prompt, never forced */
  fullscreen: boolean;
  fullscreenPrompted: boolean;
  language: Language;
  damageNumbers: DamageNumbersMode;
  screenShake: ShakeLevel;
  effects: EffectsLevel;
}

const STORAGE_KEY = 'bosshot-settings-v1';

const DEFAULTS: Settings = {
  movementMode: 'world',
  autoAim: false,
  sound: true,
  fullscreen: false,
  fullscreenPrompted: false,
  language: 'tr',
  // All hits, merged per target — merging alone cuts ~70 numbers/s to ~4
  damageNumbers: 'all',
  screenShake: 'full',
  // Medium = toned down; High keeps the original (busier) look
  effects: 'medium',
};

/** Drop stored values that are not in the allowed list (old/corrupt saves). */
function pick<T>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function load(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const merged: Settings = { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Settings>) };
    merged.damageNumbers = pick(merged.damageNumbers, DAMAGE_NUMBER_MODES, DEFAULTS.damageNumbers);
    merged.screenShake = pick(merged.screenShake, SHAKE_LEVELS, DEFAULTS.screenShake);
    merged.effects = pick(merged.effects, EFFECTS_LEVELS, DEFAULTS.effects);
    return merged;
  } catch {
    return { ...DEFAULTS };
  }
}

export const settings: Settings = load();

export function saveSettings(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* storage unavailable — ignore */
  }
}

export function toggleMovementMode(): void {
  settings.movementMode = settings.movementMode === 'world' ? 'nose' : 'world';
  saveSettings();
}

export function toggleAutoAim(): void {
  settings.autoAim = !settings.autoAim;
  saveSettings();
}

export function toggleSound(): void {
  settings.sound = !settings.sound;
  saveSettings();
}

export function toggleLanguage(): void {
  settings.language = settings.language === 'tr' ? 'en' : 'tr';
  saveSettings();
}

/** Step to the next/previous entry of an option list (wraps around). */
function step<T>(list: readonly T[], current: T, dir: 1 | -1): T {
  const i = list.indexOf(current);
  return list[(i + dir + list.length) % list.length]!;
}

export function cycleDamageNumbers(dir: 1 | -1 = 1): void {
  settings.damageNumbers = step(DAMAGE_NUMBER_MODES, settings.damageNumbers, dir);
  saveSettings();
}

export function cycleScreenShake(dir: 1 | -1 = 1): void {
  settings.screenShake = step(SHAKE_LEVELS, settings.screenShake, dir);
  saveSettings();
}

export function cycleEffects(dir: 1 | -1 = 1): void {
  settings.effects = step(EFFECTS_LEVELS, settings.effects, dir);
  saveSettings();
}
